import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus } from 'lucide-react';
import { useCompany, useCompanies } from '@/composables/crm/useCompanies';
import { useNotes } from '@/composables/crm/useNotes';
import { useActivities } from '@/composables/crm/useActivities';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate, formatCurrency, formatNumber, formatFullName } from '@/lib/formatters';
import { StatusBadge } from '@/components/StatusBadge';
import { useState } from 'react';

const activityTypes = [
  { label: 'Call', value: 'CALL' },
  { label: 'Email', value: 'EMAIL' },
  { label: 'Meeting', value: 'MEETING' },
  { label: 'Note', value: 'NOTE' },
  { label: 'Task', value: 'TASK' },
];

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { company, isLoading, contacts, deals, activities, notes, invalidate } = useCompany(id!);
  const { deleteCompany } = useCompanies();
  const { createNote } = useNotes();
  const { createActivity } = useActivities();

  const [showDelete, setShowDelete] = useState(false);
  const [noteText, setNoteText] = useState('');

  const [showActivity, setShowActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'CALL', subject: '', description: '' });

  if (isLoading) return <LoadingSpinner />;
  if (!company) return <div className="p-8 text-center">Company not found</div>;

  return (
    <PageContainer backTo
      title={company.name}
      description={company.industry || ''}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate(`/companies/${company.id}/edit`)}><Edit className="mr-2 h-4 w-4" />Edit</Button>
          <Button variant="destructive" onClick={() => setShowDelete(true)}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Company Info</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between"><span className="text-muted-foreground">Industry</span><span>{company.industry || '-'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Website</span><span>{company.website || '-'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Employees</span><span>{company.employeeCount ? formatNumber(company.employeeCount) : '-'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Revenue</span><span>{company.annualRevenue ? formatCurrency(company.annualRevenue) : '-'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{company.email || '-'}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{company.phone || '-'}</span></div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2">
          <Tabs defaultValue="notes">
            <TabsList>
              <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
              <TabsTrigger value="activities">Activities ({activities.length})</TabsTrigger>
              <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
              <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="space-y-4 mt-4">
              <Card>
                <CardContent className="p-4 space-y-2">
                  <Textarea placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} />
                  <div className="flex justify-end">
                    <Button size="sm" disabled={!noteText.trim() || createNote.isPending} onClick={() => { createNote.mutate({ content: noteText.trim(), companyId: company.id }, { onSuccess: () => setNoteText('') }); }}>Add Note</Button>
                  </div>
                </CardContent>
              </Card>
              {notes.map((n) => (
                <Card key={n.id}><CardContent className="p-4"><p className="text-sm">{n.content}</p><p className="text-xs text-muted-foreground mt-2">{formatDate(n.createdAt)}</p></CardContent></Card>
              ))}
              {notes.length === 0 && <p className="text-muted-foreground text-center py-8">No notes yet</p>}
            </TabsContent>

            <TabsContent value="activities" className="space-y-4 mt-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowActivity(true)}>
                  <Plus className="mr-1 h-4 w-4" /> Log Activity
                </Button>
              </div>
              {activities.map((a) => (
                <Card key={a.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{a.subject}</p>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{a.type.replace('_', ' ')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(a.occurredAt)}</p>
                        {a.description && <p className="text-sm mt-1 text-muted-foreground">{a.description}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {activities.length === 0 && <p className="text-muted-foreground text-center py-8">No activities yet</p>}
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4 mt-4">
              {contacts.map((c) => (
                <Card key={c.id}><CardContent className="p-4"><p className="font-medium">{formatFullName(c.firstName, c.lastName)}</p><p className="text-xs text-muted-foreground">{c.jobTitle} - {c.email}</p></CardContent></Card>
              ))}
              {contacts.length === 0 && <p className="text-muted-foreground text-center py-8">No contacts yet</p>}
            </TabsContent>

            <TabsContent value="deals" className="space-y-4 mt-4">
              {deals.map((d) => (
                <Card key={d.id}><CardContent className="p-4 flex justify-between"><div><p className="font-medium">{d.title}</p></div><div className="text-right"><p className="font-semibold">{formatCurrency(d.value)}</p><StatusBadge status={d.status} type="deal" /></div></CardContent></Card>
              ))}
              {deals.length === 0 && <p className="text-muted-foreground text-center py-8">No deals yet</p>}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ConfirmDialog open={showDelete} onOpenChange={setShowDelete} title="Delete Company" description="Are you sure? This cannot be undone." variant="destructive" confirmLabel="Delete" onConfirm={() => { deleteCompany.mutate(company.id); navigate('/companies'); }} isLoading={deleteCompany.isPending} />

      {/* Add Activity Dialog */}
      <FormDialog
        open={showActivity}
        onOpenChange={(open) => { setShowActivity(open); if (!open) setActivityForm({ type: 'CALL', subject: '', description: '' }); }}
        title="Log Activity"
        description="Record an interaction for this company"
        submitLabel="Log Activity"
        isSubmitting={createActivity.isPending}
        onSubmit={() => {
          if (!activityForm.subject.trim()) return;
          createActivity.mutate({ ...activityForm, companyId: company.id } as Parameters<typeof createActivity.mutate>[0], {
            onSuccess: () => { invalidate(); setShowActivity(false); setActivityForm({ type: 'CALL', subject: '', description: '' }); },
          });
        }}
      >
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Type</Label>
            <Select value={activityForm.type} onValueChange={(v) => setActivityForm((p) => ({ ...p, type: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{activityTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Subject *</Label>
            <Input className="mt-1" placeholder="e.g. Partnership discussion" value={activityForm.subject} onChange={(e) => setActivityForm((p) => ({ ...p, subject: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm">Description</Label>
            <Textarea className="mt-1" placeholder="Details..." rows={3} value={activityForm.description} onChange={(e) => setActivityForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
        </div>
      </FormDialog>
    </PageContainer>
  );
}
